import {
  ButtonVariants,
  Dialog,
  IconName,
  Text,
  getDisplayNameOrNameLabel,
  useSecurity,
  type MarketAccount,
  type UseDisclosureReturn,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useCustomRateApi } from './customRateApi';

interface ClearCustomRateDialogProps extends UseDisclosureReturn {
  marketAccount: MarketAccount;
  symbol: string;
}

interface ClearCustomRateDialogContainerProps {
  dialog?: ClearCustomRateDialogProps;
}

export function ClearCustomRateDialog({ dialog }: ClearCustomRateDialogContainerProps) {
  return dialog == null ? null : <ClearCustomRateDialogContent {...dialog} />;
}

function ClearCustomRateDialogContent({ marketAccount, symbol, ...disclosureProps }: ClearCustomRateDialogProps) {
  const security = useSecurity(symbol);
  const displaySymbol = security?.DisplaySymbol ?? symbol;
  const marketAccountDisplayName = getDisplayNameOrNameLabel(marketAccount);
  const { close } = disclosureProps;
  const { clearCustomRate } = useCustomRateApi({ displaySymbol, symbol });

  const handleClearRate = useCallback(() => {
    if (clearCustomRate(marketAccount)) {
      close();
    }
  }, [clearCustomRate, close, marketAccount]);

  return (
    <Dialog
      {...disclosureProps}
      title="Clear Custom Rate"
      headerIcon={IconName.Trash}
      showClose
      width={480}
      cancelLabel="Cancel"
      confirmLabel="Clear Rate"
      variant={ButtonVariants.Negative}
      onConfirm={handleClearRate}
      closeOnConfirm={false}
      showFooterBorder={false}
    >
      <Text>
        You are about to clear the custom rate for{' '}
        <Text as="span" color="colorTextImportant" fontWeight="fontWeightMedium">
          {displaySymbol}
        </Text>{' '}
        on{' '}
        <Text as="span" color="colorTextImportant" fontWeight="fontWeightMedium">
          {marketAccountDisplayName}
        </Text>
        .
      </Text>
    </Dialog>
  );
}
